import { useEffect, useState } from 'react';
import { uniqBy } from 'lodash-es';
import cn from 'classnames';
import { RC_DURATION, TagHelpers } from '@lambda/reebelo';
import { SearchOffers } from '@lambda/commons/apis/elasticsearch/types';
import { WarrantyT } from '@lambda/apis/src/warranty/types';
import Loading from '@/components/Loading/Loading';
import { selectWarranty } from '@/lib/collections/catalogue/helpers';
import { generateIDAttr, encodeDecodeForAttr } from '@/lib/use-cart';
import rCareIcon from '@/public/icons/cart/rCareNew.svg';
import { RecommendedItemsProps, RenderRecommendedItemsProps } from './types';
import { RecommendedItem } from './RecommendedItem';
import expressShippingIcon from '@/public/icons/cart/expressShipping.svg';
import {
  makeRecommendedItemsSeq,
  getItemQtyInCart,
  getItemFromCart,
} from './recommendedItemsUtils';
import i18n from '@/settings/i18n';
import {
  formatDeliveryCutOffTime,
  formatDeliveryDateRange,
} from '@/lib/use-delivery-date';
import { RecommendedItemSlideOver } from './RecommendedItemSlideOver';
import {
  generateReebeloCareSpecs,
  generateReebeloCareTitle,
} from '@/lib/reebelocare/helpers';
import { formatOfferUrlString } from '@/lib/offer';

const t = i18n.init();

const RenderRecommendedItems = ({
  item,
  index,
  cart,
  allRearrangedRecommendations,
}: RenderRecommendedItemsProps) => {
  const [expandedItem, setExpandedItem] = useState<SearchOffers | undefined>();
  const [isSlideOverOpen, setIsSlideOverOpen] = useState(false);

  const [showingMore, setShowMore] = useState(false);
  const rearrangedRecommendations = allRearrangedRecommendations.slice(
    0,
    showingMore === true ? 16 : 4,
  );

  return (
    <>
      <div
        className={cn('grid w-full grid-cols-1 gap-3 xl:grid-cols-2', {
          'mb-16': allRearrangedRecommendations.length <= 4,
        })}
      >
        {rearrangedRecommendations.length > 0 &&
          rearrangedRecommendations.map(
            (recommendedItem, recommendedItemIndex) => (
              <div
                className="w-full"
                key={`${recommendedItem.psku}-${recommendedItem.key}`}
              >
                <RecommendedItem
                  attributes={[]}
                  parentItem={item}
                  index={recommendedItemIndex}
                  lineItemIndex={index}
                  type="offer"
                  product={recommendedItem}
                  quantity={getItemQtyInCart({ cart, recommendedItem })}
                  lineItem={getItemFromCart({ cart, recommendedItem })}
                  selectedMerchId={
                    getItemFromCart({ cart, recommendedItem }).id
                  }
                  onClick={(type) => {
                    const { handle, tags } = recommendedItem;
                    const url = formatOfferUrlString(handle, tags);

                    switch (type) {
                      case 'OPEN':
                        window.open(url, '_blank', 'noopener,noreferrer');
                        break;
                      case 'EXPAND':
                        setExpandedItem(recommendedItem);
                        setIsSlideOverOpen(true);
                        break;
                      default:
                        break;
                    }
                  }}
                />
              </div>
            ),
          )}
      </div>
      {allRearrangedRecommendations.length > 4 && (
        <p
          className="mb-16 mt-2 w-full cursor-pointer text-center text-sm font-bold text-gray-700 hover:text-gray-700 sm:mt-3 sm:border-none sm:text-base xl:font-normal xl:text-gray-400"
          onClick={() => setShowMore(!showingMore)}
          id={
            showingMore === true
              ? `e2e-cart-view-less-btn-${index}`
              : `e2e-cart-view-more-btn-${index}`
          }
        >
          {showingMore === true ? t`View Less` : t`View More`}
        </p>
      )}

      <RecommendedItemSlideOver
        afterCloseFn={() => setExpandedItem(undefined)}
        lineItem={
          expandedItem
            ? getItemFromCart({ cart, recommendedItem: expandedItem })
            : undefined
        }
        open={isSlideOverOpen}
        product={expandedItem}
        quantity={
          expandedItem
            ? getItemQtyInCart({ cart, recommendedItem: expandedItem })
            : 0
        }
        setOpen={setIsSlideOverOpen}
      />
    </>
  );
};

export default function RecommendedItems({
  cart,
  itemPskuMap,
  recommendations,
  expressShippingInfo,
  zipcodeStateData,
  warrantyEligibilityMap,
  isLoading,
  rearrangedPskus,
}: RecommendedItemsProps) {
  const rCareTitle = generateReebeloCareTitle();
  const rCareSpecs = [generateReebeloCareSpecs()];

  const [warrantyMap, setWarrantyMap] = useState<
    Record<string, WarrantyT | null>
  >({});

  useEffect(() => {
    const fetchWarranties = async () => {
      if (!warrantyEligibilityMap) return;

      const warranties = await Promise.all(
        Object.entries(warrantyEligibilityMap).map(
          async ([psku, isEligible]) => {
            if (!isEligible) return [psku, null];
            const item = itemPskuMap[psku];

            if (!item) return [psku, null];

            const {
              merchandise: {
                priceV2: { amount },
                product: { productType },
              },
            } = item;
            const price = Number(amount);

            const variant = TagHelpers.extractTags(
              itemPskuMap[psku].merchandise.product.tags,
            );
            const finalPrice = TagHelpers.calculatePriceAfterSubsidy(
              price,
              variant.subsidy,
            );

            const warranty = warrantyEligibilityMap?.[psku]
              ? await selectWarranty(
                  finalPrice,
                  RC_DURATION['24_MONTH'],
                  productType,
                )
              : null;

            return [psku, warranty];
          },
        ),
      );

      setWarrantyMap(Object.fromEntries(warranties));
    };

    fetchWarranties();
  }, [itemPskuMap, warrantyEligibilityMap]);

  if (isLoading) {
    return (
      <div className="flex h-full max-h-[50vh] items-center justify-center py-5">
        <Loading />
      </div>
    );
  }

  return (
    <div className="w-full pt-8">
      {recommendations &&
        recommendations.map(
          ({ psku, recommendations: recommendedItems }, index) => {
            if (!psku || !itemPskuMap[psku]) return null;

            const shouldShowRecommendedItems =
              rearrangedPskus.indexOf(psku) === index;

            const allRearrangedRecommendations = uniqBy(
              makeRecommendedItemsSeq(recommendedItems),
              'title',
            );

            const item = itemPskuMap[psku];
            const {
              merchandise: {
                sku,
                product: { title, vendor },
              },
            } = item;

            const itemName = title.split(' - ')[0];

            const productWarranty = warrantyMap[psku];
            const selectedWarranty = cart?.lines.edges
              .map((e) => e.node)
              .find(
                (l) =>
                  encodeDecodeForAttr.decode(l.attributes) === sku &&
                  l.merchandise.product.title
                    .toLowerCase()
                    .includes('reebelocare'),
              );
            const warrantyQty = selectedWarranty
              ? selectedWarranty.quantity
              : 0;
            const attributes = [
              ...encodeDecodeForAttr.encode({
                sku,
                title,
              }),
              ...(generateIDAttr(vendor, sku) as any),
            ];
            const selectedExpress = cart?.lines.edges
              .map((e) => e.node)
              .find(
                (l) =>
                  encodeDecodeForAttr.decode(l.attributes) === sku &&
                  l.merchandise.product.title
                    .toLowerCase()
                    .includes('express shipping'),
              );
            const expressQty = selectedExpress ? selectedExpress.quantity : 0;

            if (
              Object.values(recommendedItems).every((r) => r.length === 0) &&
              !productWarranty
            )
              return null;

            // check express shipping available
            const { shipping: expressShipping } =
              expressShippingInfo.find((l) => l.key === item.id) || {};
            const expressShippingAvailable =
              expressShipping?.express &&
              expressShipping.express.minDeliveryDate !== '';

            const expressDeliveryCutOffFormated = formatDeliveryCutOffTime(
              expressShipping?.express,
            );

            return (
              <div
                key={`${itemName}-${psku}-${item.id}`}
                className={cn(
                  'flex w-full flex-wrap justify-between gap-2 sm:gap-3',
                )}
              >
                <h1 className="mb-0 w-full text-base font-bold">
                  {t`Frequently bought with ${{ itemName }}`}
                </h1>
                {productWarranty && !warrantyQty ? (
                  <div
                    className="mb-0 w-full sm:mb-2"
                    key={`reebelo-care-${itemName}`}
                  >
                    <RecommendedItem
                      product={{
                        vendor: 'Reebelo',
                        title: rCareTitle,
                        specs: rCareSpecs,
                        imageSrc: rCareIcon,
                        price: productWarranty.price,
                        variantId: productWarranty.shopifyVariantId,
                      }}
                      index={index}
                      lineItemIndex={index}
                      parentItem={item}
                      type="warranty"
                      quantity={warrantyQty}
                      attributes={attributes}
                      lineItem={selectedWarranty}
                      selectedMerchId={selectedWarranty?.id}
                    />
                  </div>
                ) : null}
                {expressShipping && expressShippingAvailable && (
                  <div
                    className="mb-0 w-full sm:mb-2"
                    key={`express-shipping-${itemName}`}
                  >
                    <RecommendedItem
                      product={{
                        vendor: 'Reebelo',
                        title: 'Express Delivery',
                        imageSrc: expressShippingIcon,
                        price: expressShipping.price,
                        variantId: expressShipping.variantId,
                        deliveryDate: formatDeliveryDateRange(
                          expressShipping.express,
                          'short',
                        ),
                        dispatchMin: expressShipping.dispatch.min,
                        dispatchMax: expressShipping.dispatch.max,
                        standardMinDispatch: expressShipping.dispatch.min,
                        standardMaxDispatch: expressShipping.dispatch.max,
                        minDeliveryDate:
                          expressShipping.express.minDeliveryDate,
                        maxDeliveryDate:
                          expressShipping.express.maxDeliveryDate,
                        standardMinDeliverBy:
                          expressShipping.standard.minDeliveryDate,
                        standardMaxDeliverBy:
                          expressShipping.standard.maxDeliveryDate,
                        standardCutoffTime: expressShipping.standard.cutoffTime,
                        standardCutoffTimezone:
                          expressShipping.standard.cutoffTimezone,
                        expressCutoffTime: expressShipping.express.cutoffTime,
                        expressCutoffTimezone:
                          expressShipping.express.cutoffTimezone,
                        zipcodeStateData,
                        expressDeliveryCutOffFormated,
                      }}
                      index={index}
                      lineItemIndex={index}
                      parentItem={item}
                      type="expressShipping"
                      quantity={expressQty}
                      attributes={attributes}
                      lineItem={selectedExpress}
                      selectedMerchId={selectedExpress?.id}
                    />
                  </div>
                )}
                {shouldShowRecommendedItems && (
                  <RenderRecommendedItems
                    item={item}
                    cart={cart}
                    index={index}
                    allRearrangedRecommendations={
                      allRearrangedRecommendations.length > 0
                        ? allRearrangedRecommendations
                        : recommendedItems.popular
                    }
                  />
                )}
              </div>
            );
          },
        )}
    </div>
  );
}
